import React from 'react';
import '../../../styles/v4-theme.css';

/**
 * AttributesTab Component
 * Displays all static properties of a target in organized sections
 */
const AttributesTab = ({ target, onTimestampClick }) => {
  // Debug: Log when component renders
  console.log('📋 AttributesTab rendering:', {
    targetId: target?.id,
    hasOnTimestampClick: !!onTimestampClick,
    onTimestampClickType: typeof onTimestampClick
  });

  const renderSection = (title, fields) => {
    const hasData = fields.some(field => field.value && field.value !== 'unknown' && field.value !== 'not visible');
    if (!hasData) return null;

    return (
      <div className="v4-attributes-section" style={{ marginBottom: 'var(--space-3)' }}>
        <h3 className="v4-attributes-section-title" style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--color-vehicle)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 'var(--space-2)',
          fontFamily: 'Courier New, monospace',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <span style={{ color: 'var(--accent)' }}>▸</span>
          {title}
        </h3>
        <div className="v4-attributes-fields" style={{
          display: 'grid',
          gap: 'var(--space-2)',
          background: 'rgba(59, 130, 246, 0.05)',
          padding: 'var(--space-3)',
          borderRadius: '6px',
          border: '1px solid rgba(59, 130, 246, 0.2)'
        }}>
          {fields.map((field, idx) => {
            if (!field.value || field.value === 'unknown' || field.value === 'not visible') return null;
            return (
              <div key={idx} className="v4-attribute-field" style={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                gap: 'var(--space-2)',
                alignItems: 'center',
                padding: 'var(--space-1)',
                borderRadius: '4px',
                transition: 'background 0.15s'
              }}>
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-secondary)',
                  fontWeight: 'var(--font-semibold)',
                  fontFamily: 'Courier New, monospace'
                }}>
                  {field.label}:
                </div>
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-primary)',
                  fontWeight: 'var(--font-medium)'
                }}>
                  {/* Make timestamps clickable */}
                  {onTimestampClick && (field.label === 'First Appearance' || field.label === 'Last Appearance') ? (
                    <span 
                      className="v4-timestamp-link"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('🎬 Timestamp clicked:', field.value, field.label);
                        onTimestampClick(field.value, target);
                      }}
                      title="Click to jump to video"
                      style={{
                        cursor: 'pointer',
                        color: 'var(--accent, #7C3AED)',
                        textDecoration: 'underline',
                        fontWeight: '600'
                      }}
                    >
                      ▶ {field.value}
                    </span>
                  ) : (
                    field.value
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const attrs = target.attributes || {};
  const spatial = target.spatial || {};
  const targetType = target.type || target.target_type;

  // Session 7 Hybrid Model: Multi-appearance segments
  const appearanceSegments = target.appearance_segments || [];
  const legalStats = target.legal_report_stats || {};

  // Render appearance segments (Session 7 Hybrid Model)
  const renderAppearanceSegments = () => {
    if (appearanceSegments.length === 0) return null;

    return (
      <div className="v4-attributes-section" style={{ marginBottom: 'var(--space-3)' }}>
        <h3 className="v4-attributes-section-title" style={{
          fontSize: 'var(--text-xs)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--success)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          marginBottom: 'var(--space-2)',
          fontFamily: 'Courier New, monospace',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)'
        }}>
          <span style={{ color: 'var(--success)' }}>📍</span>
          APPEARANCE WINDOWS ({appearanceSegments.length})
        </h3>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)'
        }}>
          {appearanceSegments.map((segment, idx) => {
            const tier = segment.confidence_tier || 'verified';
            const isVerified = tier === 'verified';
            const plateCount = segment.plate_detection_count || segment.plate_detections?.length || 0;
            const duration = segment.duration_seconds ? `${segment.duration_seconds.toFixed(1)}s` : 'N/A';
            
            return (
              <div key={segment.segment_id || idx} style={{
                background: isVerified ? 'rgba(34, 197, 94, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                border: `1px solid ${isVerified ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                borderRadius: '6px',
                padding: 'var(--space-2)'
              }}>
                {/* Segment Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 'var(--space-1)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                  }}>
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-bold)',
                      color: isVerified ? 'var(--success)' : 'var(--warning)',
                      fontFamily: 'Courier New, monospace'
                    }}>
                      {isVerified ? '✓ VERIFIED' : '○ TRACKED'}
                    </span>
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-muted)',
                      fontFamily: 'Courier New, monospace'
                    }}>
                      Window #{idx + 1}
                    </span>
                  </div>
                  {plateCount > 0 && (
                    <span style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--success)',
                      fontWeight: 'var(--font-semibold)',
                      fontFamily: 'Courier New, monospace'
                    }}>
                      {plateCount} plate reads
                    </span>
                  )}
                </div>
                
                {/* Segment Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: 'var(--space-2)',
                  fontSize: 'var(--text-xs)',
                  fontFamily: 'Courier New, monospace'
                }}>
                  <div>
                    <span style={{ color: 'var(--text-secondary)' }}>Start: </span>
                    {onTimestampClick ? (
                      <span 
                        className="v4-timestamp-link"
                        onClick={(e) => {
                          e.preventDefault();
                          onTimestampClick(segment.first_seen, target);
                        }}
                        style={{
                          cursor: 'pointer',
                          color: 'var(--accent)',
                          textDecoration: 'underline'
                        }}
                      >
                        {segment.first_seen}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-primary)' }}>{segment.first_seen}</span>
                    )}
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)' }}>End: </span>
                    <span style={{ color: 'var(--text-primary)' }}>{segment.last_seen}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-secondary)' }}>Duration: </span>
                    <span style={{ color: 'var(--text-primary)' }}>{duration}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Legal Stats Summary */}
        {(legalStats.total_plate_detections > 0 || legalStats.verified_duration > 0) && (
          <div style={{
            marginTop: 'var(--space-2)',
            padding: 'var(--space-2)',
            background: 'rgba(59, 130, 246, 0.08)',
            borderRadius: '4px',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            fontSize: 'var(--text-xs)',
            fontFamily: 'Courier New, monospace'
          }}>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
              📋 Legal Evidence Summary:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
              {legalStats.total_plate_detections > 0 && (
                <span style={{ color: 'var(--success)' }}>
                  <strong>{legalStats.total_plate_detections}</strong> verified reads
                </span>
              )}
              {legalStats.verified_duration > 0 && (
                <span style={{ color: 'var(--text-primary)' }}>
                  <strong>{legalStats.verified_duration.toFixed(1)}s</strong> verified
                </span>
              )}
              {legalStats.tracked_duration > 0 && (
                <span style={{ color: 'var(--warning)' }}>
                  <strong>{legalStats.tracked_duration.toFixed(1)}s</strong> tracked
                </span>
              )}
              {legalStats.confidence_avg && (
                <span style={{ color: 'var(--text-muted)' }}>
                  Avg: {(legalStats.confidence_avg * 100).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Vehicle-specific sections
  if (targetType === 'vehicle') {
    return (
      <div className="v4-attributes-content" style={{ padding: 'var(--space-3)' }}>
        {renderSection('VEHICLE IDENTIFICATION', [
          { label: 'Make', value: attrs.make },
          { label: 'Model', value: attrs.model },
          { label: 'Year', value: attrs.year },
          { label: 'Color', value: attrs.color },
          { label: 'Color Secondary', value: attrs.color_secondary },
          { label: 'Vehicle Type', value: attrs.vehicle_type || attrs.type },
          { label: 'Body Style', value: attrs.body_style }
        ])}

        {renderSection('LICENSE PLATE', [
          { label: 'Number', value: attrs.license_plate },
          { label: 'State', value: attrs.plate_state },
          { label: 'Visibility', value: attrs.plate_visibility }
        ])}

        {renderSection('DAMAGE ASSESSMENT', [
          { label: 'Overall', value: attrs.damage },
          { label: 'Severity', value: attrs.damage_severity },
          { label: 'Front', value: attrs.damage_front },
          { label: 'Rear', value: attrs.damage_rear },
          { label: 'Left Side', value: attrs.damage_left },
          { label: 'Right Side', value: attrs.damage_right }
        ])}

        {renderSection('NOTABLE FEATURES', [
          { label: 'Equipment', value: attrs.equipment },
          { label: 'Markings', value: attrs.markings },
          { label: 'Modifications', value: attrs.modifications },
          { label: 'Overall Condition', value: attrs.overall_condition },
          { label: 'Distinguishing', value: attrs.notable_features }
        ])}

        {renderSection('SPATIAL INFORMATION', [
          { label: 'First Appearance', value: spatial.first_appearance },
          { label: 'Last Appearance', value: spatial.last_appearance },
          { label: 'Total Duration', value: spatial.total_visible_duration },
          { label: 'Visibility', value: spatial.visibility }
        ])}

        {/* Session 7: Multi-Appearance Windows */}
        {renderAppearanceSegments()}

        {renderSection('CONFIDENCE SCORES', [
          { label: 'Overall', value: target.confidence ? `${Math.round(target.confidence * 100)}%` : null },
          { label: 'Identification', value: attrs.confidence_identification ? `${Math.round(attrs.confidence_identification * 100)}%` : null },
          { label: 'Attributes', value: attrs.confidence_attributes ? `${Math.round(attrs.confidence_attributes * 100)}%` : null }
        ])}
      </div>
    );
  }

  // Environment-specific sections
  if (targetType === 'environment') {
    return (
      <div className="v4-attributes-content" style={{ padding: 'var(--space-3)' }}>
        {renderSection('LOCATION', [
          { label: 'Location Type', value: attrs.location_type },
          { label: 'Setting', value: attrs.setting },
          { label: 'Visibility', value: attrs.visibility },
          { label: 'Characteristics', value: attrs.characteristics }
        ])}

        {renderSection('WEATHER CONDITIONS', [
          { label: 'Condition', value: attrs.weather },
          { label: 'Precipitation', value: attrs.precipitation }
        ])}

        {renderSection('LIGHTING', [
          { label: 'Level', value: attrs.lighting },
          { label: 'Source', value: attrs.lighting_source },
          { label: 'Time of Day', value: attrs.time_of_day }
        ])}

        {renderSection('SPATIAL INFORMATION', [
          { label: 'First Appearance', value: spatial.first_appearance },
          { label: 'Last Appearance', value: spatial.last_appearance },
          { label: 'Total Duration', value: spatial.total_visible_duration }
        ])}
      </div>
    );
  }

  // Videographer-specific sections
  if (targetType === 'videographer') {
    return (
      <div className="v4-attributes-content" style={{ padding: 'var(--space-3)' }}>
        {renderSection('DEVICE INFORMATION', [
          { label: 'Device Type', value: attrs.device_type },
          { label: 'Camera Type', value: attrs.camera_type },
          { label: 'Quality', value: attrs.quality },
          { label: 'Resolution', value: attrs.resolution }
        ])}

        {renderSection('CAMERA CHARACTERISTICS', [
          { label: 'Camera Movement', value: attrs.camera_movement },
          { label: 'Stability', value: attrs.stability },
          { label: 'Recording Angle', value: attrs.recording_angle },
          { label: 'Distance to Scene', value: attrs.distance_to_scene },
          { label: 'Mounting', value: attrs.mounting },
          { label: 'Height', value: attrs.height },
          { label: 'Angle', value: attrs.angle }
        ])}

        {renderSection('SPATIAL INFORMATION', [
          { label: 'First Appearance', value: spatial.first_appearance },
          { label: 'Last Appearance', value: spatial.last_appearance },
          { label: 'Total Duration', value: spatial.total_visible_duration }
        ])}
      </div>
    );
  }

  // Generic fallback for any target type
  const allAttributes = Object.entries(attrs).filter(
    ([key, value]) => value && value !== 'unknown' && value !== 'not visible'
  );

  return (
    <div className="v4-attributes-content" style={{ padding: 'var(--space-3)' }}>
      {allAttributes.length > 0 ? (
        renderSection('ALL ATTRIBUTES', allAttributes.map(([key, value]) => ({
          label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: typeof value === 'object' ? JSON.stringify(value) : String(value)
        })))
      ) : (
        <div className="v4-empty-state">
          <div className="v4-empty-state-icon">📋</div>
          <div className="v4-empty-state-title">No Attributes Available</div>
          <div className="v4-empty-state-description">
            No detailed attributes were extracted for this target.
          </div>
        </div>
      )}

      {renderSection('SPATIAL INFORMATION', [
        { label: 'First Appearance', value: spatial.first_appearance },
        { label: 'Last Appearance', value: spatial.last_appearance },
        { label: 'Total Duration', value: spatial.total_visible_duration }
      ])}
    </div>
  );
};

export default AttributesTab;

